import { Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface DuplicateDialogData {
  duplicates: any[];
  summary: string;
  clipboardText: string;
}

@Component({
  selector: 'app-duplicate-dialog',
  standalone: false,
  templateUrl: './duplicate-dialog.component.html',
  styleUrl: './duplicate-dialog.component.scss'
})
export class DuplicateDialogComponent {
  copySuccess = false;
  copyError = false;
  private resetTimer?: number;

  constructor(
    @Optional() private dialogRef: MatDialogRef<DuplicateDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: DuplicateDialogData
  ) {}

  copySummary(): void {
    const text = this.data?.clipboardText;
    if (!text) {
      this.markCopyError();
      return;
    }

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => this.markCopySuccess())
        .catch(err => {
          console.error('Failed to copy duplicate summary', err);
          this.fallbackCopy(text);
        });
    } else {
      this.fallbackCopy(text);
    }
  }

  cancel(): void {
    this.clearTimer();
    this.dialogRef?.close('cancel');
  }

  proceed(): void {
    this.clearTimer();
    this.dialogRef?.close('proceed');
  }

  private fallbackCopy(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      const success = document.execCommand('copy');
      if (success) {
        this.markCopySuccess();
      } else {
        this.markCopyError();
      }
    } catch (err) {
      console.error('Failed to copy duplicate summary', err);
      this.markCopyError();
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private markCopySuccess(): void {
    this.copySuccess = true;
    this.copyError = false;
    this.startResetTimer();
  }

  private markCopyError(): void {
    this.copyError = true;
    this.copySuccess = false;
    this.startResetTimer();
  }

  private startResetTimer(): void {
    this.clearTimer();
    this.resetTimer = window.setTimeout(() => {
      this.copySuccess = false;
      this.copyError = false;
    }, 2500);
  }

  private clearTimer(): void {
    if (this.resetTimer) {
      window.clearTimeout(this.resetTimer);
      this.resetTimer = undefined;
    }
  }
}
